import React, {useState, useRef, useEffect} from 'react';
import {Box, Stack} from '@mui/material';
import {
  Body,
  Headline,
  Icon,
  designSystemToken,
  IconName,
} from '@lightricks/react-design-system';

interface IconWithBackgroundProps {
  iconName: IconName;
  style?: React.CSSProperties;
}

function IconWithBackground({iconName, style}: IconWithBackgroundProps) {
  return (
    <Box
      width="40px"
      height="40px"
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderRadius="8px"
      border={`1px solid ${designSystemToken('semantic.bg.tertiary')}`}
      sx={{
        backgroundColor: designSystemToken('semantic.bg.alpha-secondary'),
        ...style,
      }}
    >
      <Icon
        name={iconName}
        size="medium"
        appearance="neutral"
        color={designSystemToken('semantic.fg.white')}
      />
    </Box>
  );
}

export interface FeatureCardProps {
  title: string;
  description: string;
  iconName: IconName;
  videoPath: string;
  thumbnailPath: string;
  onClick: () => void;
}

function FeatureCard(props: FeatureCardProps) {
  const {title, description, iconName, videoPath, thumbnailPath, onClick} =
    props;
  const borderRadius = '16px';
  const [isHovered, setIsHovered] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleVideoLoad = () => {
    setIsVideoLoaded(true);
  };

  const handleVideoError = (error: unknown) => {
    // eslint-disable-next-line no-console
    console.error('Error loading video:', error);
    setIsVideoLoaded(false);
  };

  useEffect(() => {
    if (!videoRef.current || !isVideoLoaded) return;
    if (isHovered) {
      videoRef.current.play();
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [isHovered, isVideoLoaded]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="352px"
      borderRadius={borderRadius}
      border={`1px solid ${designSystemToken('semantic.bg.tertiary')}`}
      sx={{
        background: designSystemToken('semantic.bg.secondary'),
        boxShadow:
          '0px 0px 1px 0px rgba(14, 21, 38, 0.08), 0px 2px 32px 0px rgba(14, 21, 38, 0.06)',
        cursor: 'pointer',
      }}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Box
        position="relative"
        sx={{
          borderRadius: `${borderRadius} ${borderRadius} 0 0`,
          overflow: 'hidden',
        }}
      >
        <IconWithBackground
          iconName={iconName}
          style={{position: 'absolute', top: '10px', left: '10px', zIndex: 1}}
        />
        <Box
          component="video"
          ref={videoRef}
          src={videoPath}
          poster={thumbnailPath}
          width="100%"
          height="211px"
          sx={{
            objectFit: 'cover',
            display: 'block',
            visibility: isVideoLoaded ? 'visible' : 'hidden',
            position: 'absolute',
            opacity: isHovered ? 1 : 0,
          }}
          preload="auto"
          muted
          playsInline
          loop
          onLoadedData={handleVideoLoad}
          onError={handleVideoError}
        />
        <Box
          component="img"
          src={thumbnailPath}
          alt=""
          width="100%"
          height="211px"
          sx={{
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </Box>
      <Stack direction="column" gap="8px" padding="12px">
        <Headline
          size="sm"
          color={designSystemToken('semantic.fg.primary')}
          sx={{textAlign: 'center'}}
        >
          {title}
        </Headline>
        <Body
          size="sm"
          color={designSystemToken('semantic.fg.secondary')}
          sx={{textAlign: 'center'}}
        >
          {description}
        </Body>
      </Stack>
    </Box>
  );
}

export default FeatureCard;
